import React, { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import ResultsSection from './components/ResultsSection';
import FeaturesSection from './components/FeaturesSection';
import NoResultsSection from './components/NoResultsSection';
import Footer from './components/Footer';
import DonationButton, { DonationSection } from './components/DonationButton';
import ContactSection from './components/ContactSection';
import { findSaintByName } from './data/saintsDatabase';
import { addToSearchHistory } from './utils/searchHistory';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppContent = () => {
  const { language } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [searchedName, setSearchedName] = useState('');
  const [showNoResults, setShowNoResults] = useState(false);
  const resultsRef = useRef(null);

  const scrollToResults = () => {
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // Function to generate images and prayer
  const generateContent = async (saintName, archangelName) => {
    try {
      // Generate Saint Image
      const saintImagePromise = axios.post(`${API}/generate-image`, {
        saint_name: saintName,
        image_type: 'saint',
        language: language
      }).then(response => {
        setResult(prev => ({
          ...prev,
          saintImage: response.data.image_url || `data:image/png;base64,${response.data.image_base64}`,
          isGeneratingSaintImage: false
        }));
      }).catch(error => {
        console.error('Error generating saint image:', error);
        setResult(prev => ({
          ...prev,
          isGeneratingSaintImage: false
        }));
        toast.error(language === 'fr' ? 'Erreur lors de la génération de l\'image du saint' : 'Error generating saint image');
      });

      // Generate Archangel Image
      const archangelImagePromise = axios.post(`${API}/generate-image`, {
        saint_name: archangelName,
        image_type: 'archangel',
        language: language
      }).then(response => {
        setResult(prev => ({
          ...prev,
          archangelImage: response.data.image_url || `data:image/png;base64,${response.data.image_base64}`,
          isGeneratingArchangelImage: false
        }));
      }).catch(error => {
        console.error('Error generating archangel image:', error);
        setResult(prev => ({
          ...prev,
          isGeneratingArchangelImage: false
        }));
        toast.error(language === 'fr' ? 'Erreur lors de la génération de l\'image de l\'archange' : 'Error generating archangel image');
      });

      // Generate Prayer
      const prayerPromise = axios.post(`${API}/generate-prayer`, {
        saint_name: saintName,
        archangel_name: archangelName,
        language: language
      }).then(response => {
        setResult(prev => ({
          ...prev,
          prayer: response.data.prayer,
          isGeneratingPrayer: false
        }));
      }).catch(error => {
        console.error('Error generating prayer:', error);
        setResult(prev => ({
          ...prev,
          isGeneratingPrayer: false
        }));
        toast.error(language === 'fr' ? 'Erreur lors de la génération de la prière' : 'Error generating prayer');
      });

      // Wait for all to complete
      await Promise.allSettled([saintImagePromise, archangelImagePromise, prayerPromise]);

    } catch (error) {
      console.error('Error during generation:', error);
    }
  };

  const handleSearch = useCallback(async (name) => {
    if (!name.trim()) return;

    setIsLoading(true);
    setShowNoResults(false);
    setSearchedName(name);
    
    // Add to search history
    addToSearchHistory(name);

    // Always use AI to get detailed saint information
    try {
      toast.info(
        language === 'fr' 
          ? 'Recherche des informations détaillées du saint...' 
          : 'Searching for detailed saint information...'
      );

      const response = await axios.post(`${API}/lookup-saint`, {
        first_name: name,
        language: language
      });

      if (response.data.found && response.data.saint_name) {
        // AI found a saint with detailed info
        const initialResult = {
          saintName: response.data.saint_name,
          feastDay: response.data.feast_day,
          archangel: response.data.archangel,
          history: response.data.history,
          // Detailed fields
          introduction: response.data.introduction,
          epoch: response.data.epoch,
          location: response.data.location,
          origin: response.data.origin,
          lifeStory: response.data.life_story,
          symbolism: response.data.symbolism || [],
          // Generation states
          prayer: null,
          saintImage: null,
          archangelImage: null,
          isGeneratingSaintImage: true,
          isGeneratingArchangelImage: true,
          isGeneratingPrayer: true
        };

        setResult(initialResult);
        setIsLoading(false);
        scrollToResults();

        // Generate images and prayer
        await generateContent(response.data.saint_name, response.data.archangel);
      } else {
        // No saint found
        setResult(null);
        setShowNoResults(true);
        setIsLoading(false);
        scrollToResults();
      }
    } catch (error) {
      console.error('Error looking up saint:', error);
      
      // Fallback to local database if AI fails
      const saintData = findSaintByName(name);
      if (saintData) {
        const initialResult = {
          saintName: saintData.saint[language],
          feastDay: saintData.feastDay[language],
          archangel: saintData.archangel[language],
          history: saintData.history[language],
          // No detailed fields from local DB
          introduction: '',
          epoch: '',
          location: '',
          origin: '',
          lifeStory: '',
          symbolism: [],
          prayer: null,
          saintImage: null,
          archangelImage: null,
          isGeneratingSaintImage: true,
          isGeneratingArchangelImage: true,
          isGeneratingPrayer: true
        };

        setResult(initialResult);
        setIsLoading(false);
        scrollToResults();
        
        toast.warning(
          language === 'fr' 
            ? 'Informations de base chargées (détails indisponibles)' 
            : 'Basic info loaded (details unavailable)'
        );

        await generateContent(saintData.saint[language], saintData.archangel[language]);
      } else {
        setResult(null);
        setShowNoResults(true);
        setIsLoading(false);
        scrollToResults();
        toast.error(
          language === 'fr' 
            ? 'Erreur lors de la recherche du saint' 
            : 'Error looking up saint'
        );
      }
    }
  }, [language]);

  const handleNewSearch = useCallback((suggestedName = '') => {
    setResult(null);
    setShowNoResults(false);
    setSearchedName('');
    
    if (suggestedName) {
      handleSearch(suggestedName);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [handleSearch]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main>
        <HeroSection onSearch={handleSearch} isLoading={isLoading} />
        
        <div ref={resultsRef}>
          {showNoResults && (
            <NoResultsSection 
              searchedName={searchedName} 
              onNewSearch={handleNewSearch} 
            />
          )}
          
          {result && (
            <ResultsSection 
              result={result} 
              onNewSearch={() => handleNewSearch('')} 
            />
          )}
        </div>
        
        <FeaturesSection />
        <ContactSection />
      </main>
      
      <Footer />
      <DonationButton variant="floating" />
      <Toaster position="bottom-right" richColors />
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

export default App;
